import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useContext, useEffect, useState } from 'react';
import { APIContext } from '../../Contexts/APIContext';
import MaintenanceDialogTitle from './MaintenanceDialogTitle';
import { ReleaseVersion } from './ReleaseManagementTypes';

interface Props {
    open: boolean;
    onClose: () => void;
    onCreated: () => void;
}

export default function CreateDataVersionDialog({ open, onClose, onCreated }: Props) {
    const { get, post, showMessage } = useContext(APIContext);
    const [versions, setVersions] = useState<ReleaseVersion[]>([]);
    const [versionName, setVersionName] = useState('');
    const [start, setStart] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) get('Admin', 'GetReleaseVersions', setVersions);
    }, [open]);

    const close = () => {
        if (submitting) return;
        setVersionName('');
        setStart('');
        onClose();
    };

    const create = async () => {
        setSubmitting(true);
        await post('Admin', 'CreateDataVersion', { versionName, start }, () => {
            showMessage('success', 'The future data version was created.');
            setVersionName('');
            setStart('');
            onCreated();
            onClose();
        });
        setSubmitting(false);
    };

    const latest = versions[0];
    const validStart = start !== '' && new Date(start).getTime() > Date.now() &&
        (!latest || new Date(start).getTime() > new Date(latest.start).getTime());

    return <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
        <MaintenanceDialogTitle
            icon={<CalendarMonthOutlinedIcon />}
            title="Create a data version"
            description="Schedule a new future TRICS release"
        />
        <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
                <Alert severity="info">
                    This creates a future release only. It does not change an existing release or make the new version current immediately.
                </Alert>
                {latest && <Typography variant="body2" color="text.secondary">
                    Latest version: {latest.versionName}, starting {new Date(latest.start).toLocaleString('en-GB')}.
                </Typography>}
                <TextField
                    autoFocus
                    fullWidth
                    label="Version name"
                    value={versionName}
                    disabled={submitting}
                    onChange={event => setVersionName(event.target.value)}
                />
                <TextField
                    fullWidth
                    label="Release start"
                    type="datetime-local"
                    value={start}
                    disabled={submitting}
                    InputLabelProps={{ shrink: true }}
                    onChange={event => setStart(event.target.value)}
                    error={start !== '' && !validStart}
                    helperText={start !== '' && !validStart ? 'Choose a future date and time after the latest version.' : 'The version becomes selectable when this local date and time is reached.'}
                />
            </Stack>
        </DialogContent>
        <DialogActions>
            <Button onClick={close} disabled={submitting}>Cancel</Button>
            <Button variant="contained" onClick={create} disabled={submitting || versionName.trim() === '' || !validStart}>Create Version</Button>
        </DialogActions>
    </Dialog>;
}
